import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { EditableField, DocumentPreviewResponse } from '@/services/documents';

export interface DocumentFieldsModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (editedPayload: Record<string, unknown>) => void;
  previewData: DocumentPreviewResponse | null;
  isLoading?: boolean;
  isGenerating?: boolean;
  /** Custom label for the confirm button (default: "Generate Document") */
  confirmLabel?: string;
  /** Custom label shown while generating (default: "Generating...") */
  generatingLabel?: string;
  /** Custom dialog title (default: "Review Document Fields") */
  title?: string;
  /** Custom subtitle shown below title */
  subtitle?: string;
  /** If true, the Cancel button is hidden (modal cannot be dismissed) */
  hideCancel?: boolean;
  /** Optional callback for skipping document generation while still completing the action */
  onSkip?: () => void;
  /** Custom label for the skip button (default: "Skip") */
  skipLabel?: string;
  /** Custom label for the cancel/back button (default: "Cancel") */
  cancelLabel?: string;
  /** Optional content rendered above generated editable fields */
  prependContent?: React.ReactNode;
  /** Optional content rendered below generated editable fields */
  appendContent?: React.ReactNode;
  /** Disables confirm button when external validation is not satisfied */
  isConfirmDisabled?: boolean;
}

interface FieldValue {
  dataSource: string;
  value: unknown;
}

/**
 * Set a value at a nested path in an object.
 * Supports dot notation ("asset.nomenclature") and array bracket notation ("items[0].unitPrice").
 */
function setValueByPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  // Normalize bracket notation: "items[0].unitPrice" → "items.0.unitPrice"
  const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.');
  let current: Record<string, unknown> = obj;

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (part === undefined) continue;
    if (current[part] === undefined || current[part] === null) {
      const nextPart = parts[i + 1];
      current[part] = nextPart !== undefined && /^\d+$/.test(nextPart) ? [] : {};
    }
    current = current[part] as Record<string, unknown>;
  }

  const lastPart = parts[parts.length - 1];
  if (lastPart !== undefined) {
    current[lastPart] = value;
  }
}

/**
 * Deep clone an object
 */
function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj)) as T;
}

export const DocumentFieldsModal = ({
  open,
  onClose,
  onConfirm,
  previewData,
  isLoading = false,
  isGenerating = false,
  confirmLabel = 'Generate Document',
  generatingLabel = 'Generating...',
  title,
  subtitle,
  hideCancel = false,
  onSkip,
  skipLabel = 'Skip',
  cancelLabel = 'Cancel',
  prependContent,
  appendContent,
  isConfirmDisabled = false,
}: DocumentFieldsModalProps) => {
  const [fieldValues, setFieldValues] = useState<Map<string, FieldValue>>(new Map());
  const [validationError, setValidationError] = useState<string | null>(null);

  // Initialize field values when preview data changes
  useEffect(() => {
    if (!previewData) {
      setFieldValues(new Map());
      return;
    }

    const initialValues = new Map<string, FieldValue>();
    for (const field of previewData.editableFields) {
      initialValues.set(field.pdfFieldName, {
        dataSource: field.dataSource,
        value:
          field.currentValue ?? field.defaultValue ?? (field.dataType === 'boolean' ? false : ''),
      });
    }
    setFieldValues(initialValues);
  }, [previewData]);

  // Group fields by section
  const fieldsBySection = useMemo(() => {
    if (!previewData) return new Map<string, EditableField[]>();

    const grouped = new Map<string, EditableField[]>();
    for (const field of previewData.editableFields) {
      const section = field.section;
      if (!grouped.has(section)) {
        grouped.set(section, []);
      }
      grouped.get(section)?.push(field);
    }

    // Sort fields within each section by displayOrder
    for (const fields of grouped.values()) {
      fields.sort((a, b) => a.displayOrder - b.displayOrder);
    }

    return grouped;
  }, [previewData]);

  // Order sections according to the previewData.sections array
  const orderedSections = useMemo(() => {
    if (!previewData) return [];
    return previewData.sections.filter((section) => fieldsBySection.has(section));
  }, [previewData, fieldsBySection]);

  const handleFieldChange = useCallback(
    (pdfFieldName: string, dataSource: string, value: unknown) => {
      setFieldValues((prev) => {
        const next = new Map(prev);
        next.set(pdfFieldName, { dataSource, value });
        return next;
      });
    },
    [],
  );

  const handleConfirm = useCallback(() => {
    if (!previewData) return;

    // Clear any previous validation errors
    setValidationError(null);

    // Validate required fields
    const missingFields: string[] = [];
    for (const field of previewData.editableFields) {
      if (field.required) {
        const fieldValue = fieldValues.get(field.pdfFieldName);
        const value = fieldValue?.value;

        // Check if value is empty (null, undefined, empty string, or false for non-boolean fields)
        const isEmpty =
          value === null ||
          value === undefined ||
          (typeof value === 'string' && value.trim() === '') ||
          (field.dataType !== 'boolean' && value === false);

        if (isEmpty) {
          missingFields.push(field.displayLabel);
        }
      }
    }

    if (missingFields.length > 0) {
      // Show inline error for missing required fields
      setValidationError(
        `Please fill in the following required fields: ${missingFields.join(', ')}`,
      );
      return;
    }

    // Build the edited payload by applying all field changes to the original payload
    const editedPayload = deepClone(previewData.transformedPayload);

    for (const [, fieldValue] of fieldValues) {
      setValueByPath(editedPayload, fieldValue.dataSource, fieldValue.value);
    }

    onConfirm(editedPayload);
  }, [previewData, fieldValues, onConfirm]);

  const renderField = useCallback(
    (field: EditableField) => {
      const fieldValue = fieldValues.get(field.pdfFieldName);
      const value = fieldValue?.value ?? '';

      if (field.dataType === 'boolean') {
        return (
          <FormControlLabel
            key={field.pdfFieldName}
            control={
              <Checkbox
                checked={Boolean(value)}
                onChange={(e) =>
                  handleFieldChange(field.pdfFieldName, field.dataSource, e.target.checked)
                }
                disabled={isGenerating}
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  {field.displayLabel}
                  {field.required && (
                    <Box component="span" sx={{ color: 'error.main' }}>
                      {' '}
                      *
                    </Box>
                  )}
                </Typography>
                {field.helpText && (
                  <Typography variant="caption" color="text.secondary">
                    {field.helpText}
                  </Typography>
                )}
              </Box>
            }
          />
        );
      }

      // For string/number/date fields, render a TextField
      const isMultiline =
        field.dataType === 'string' &&
        (field.dataSource.includes('nomenclature') ||
          field.dataSource.includes('address') ||
          field.dataSource.includes('loa') ||
          String(value).includes('\n'));

      return (
        <TextField
          key={field.pdfFieldName}
          label={field.displayLabel}
          value={String(value)}
          onChange={(e) => handleFieldChange(field.pdfFieldName, field.dataSource, e.target.value)}
          fullWidth
          required={field.required}
          disabled={isGenerating}
          helperText={field.helpText}
          placeholder={field.placeholder}
          multiline={isMultiline}
          minRows={isMultiline ? 2 : undefined}
          maxRows={isMultiline ? 6 : undefined}
          size="small"
          sx={{ mb: 2 }}
        />
      );
    },
    [fieldValues, handleFieldChange, isGenerating],
  );

  return (
    <Dialog
      open={open}
      onClose={isGenerating ? undefined : onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="document-fields-dialog-title"
    >
      <DialogTitle id="document-fields-dialog-title">
        {isLoading ? (
          'Loading Document Fields...'
        ) : previewData ? (
          <Box>
            <Typography variant="h6" component="span">
              {title || 'Review Document Fields'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle || `${previewData.documentName} (v${previewData.version})`}
            </Typography>
          </Box>
        ) : (
          'Document Preview'
        )}
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !previewData ? (
          <Typography color="text.secondary">No preview data available.</Typography>
        ) : (
          <Stack spacing={3}>
            {prependContent}

            {!subtitle && (
              <Typography variant="body2" color="text.secondary">
                Review and edit the fields below before generating the document. Required fields are
                marked with an asterisk (*).
              </Typography>
            )}

            {validationError && (
              <Alert severity="error" onClose={() => setValidationError(null)}>
                {validationError}
              </Alert>
            )}

            {orderedSections.map((section, index) => {
              const fields = fieldsBySection.get(section);
              if (!fields || fields.length === 0) return null;

              return (
                <Box key={section}>
                  {index > 0 && <Divider sx={{ mb: 2 }} />}
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
                    {section}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                      gap: 2,
                    }}
                  >
                    {fields.map(renderField)}
                  </Box>
                </Box>
              );
            })}

            {appendContent}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!hideCancel && (
          <Button onClick={onClose} disabled={isGenerating}>
            {cancelLabel}
          </Button>
        )}
        {onSkip && (
          <Button onClick={onSkip} disabled={isGenerating} variant="contained" color="secondary">
            {skipLabel}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading || !previewData || isGenerating || isConfirmDisabled}
          startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isGenerating ? generatingLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
